import { cookies } from "next/headers"
import { NextResponse } from "next/server"
import { z } from "zod"
import { getSession } from "@/services/auth-service"
import {
  applyCartTokenCookie,
  applyWooCartSessionCookies,
  buildForwardCookieHeader,
  readCartTokenCookie,
  readWooGuestSessionCookies,
  updateWordPressCartShippingAddress,
} from "@/services/cart-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const shippingAddressPayloadSchema = z.object({
  country: z.string().trim().min(1),
  state: z.string().trim().min(1),
  city: z.string().trim(),
  postcode: z.string().trim(),
})

const getForwardedCookieHeader = async (): Promise<string> => {
  const session = await getSession()
  const cookieStore = await cookies()

  return buildForwardCookieHeader({
    authSessionToken: session?.wordpressSessionCookie ?? null,
    wooGuestSessionCookies: readWooGuestSessionCookies(cookieStore),
  })
}

const getForwardedCartToken = async (): Promise<string | null> => {
  const cookieStore = await cookies()

  return readCartTokenCookie(cookieStore)
}

export const POST = async (request: Request) => {
  const payload = await request.json().catch(() => null)
  const parsedPayload = shippingAddressPayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para atualizar endereco de entrega.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  const forwardedCookieHeader = await getForwardedCookieHeader()
  const forwardedCartToken = await getForwardedCartToken()

  try {
    const result = await updateWordPressCartShippingAddress(
      parsedPayload.data,
      forwardedCookieHeader,
      forwardedCartToken
    )

    const response = NextResponse.json(
      {
        ok: result.ok,
        message: result.message,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "CART_ADDRESS_UPDATE_FAILED",
        message: "Nao foi possivel atualizar endereco de entrega.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
